import React, { useContext, useState, useEffect } from "react";
import {
  Chip,
  Typography,
  Grid,
  Box,
  Tooltip,
  Card,
  CardContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  CardMedia,
} from "@material-ui/core";
import {
  Close,
  ViewModule,
  ViewList,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";
import { useCookies } from "react-cookie";
import { formatPhoneNumber } from "../../../Gfunc";
import ParamContext from "../../../Contexte";

const statuses = [
  {
    id: 4,
    label: "Succès",
    color: "#219a13",
  },
  { id: 3, label: "Échec", color: "#d32f2f" },
  {
    id: 1,
    label: "En attente",
    color: "#FFC000",
  },
];

// Function to get light background color from status color
const getLightBackgroundColor = (color) => {
  return color + "15";
};

// Function to get darker background color for selected state
const getSelectedBackgroundColor = (color) => {
  return color + "30";
};

export default function OrderHistoryBox({ orders }) {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cookies, setCookie] = useCookies(["userViewMode"]);
  const param = useContext(ParamContext);

  // Get user ID from localStorage
  const getUserId = () => {
    return localStorage.getItem("uid"); // Adjust this based on where you store user ID
  };

  // Load view mode from cookies on component mount
  useEffect(() => {
    const userId = getUserId();
    if (userId && cookies.userViewMode && cookies.userViewMode[userId]) {
      setViewMode(cookies.userViewMode[userId]);
    }
  }, [cookies.userViewMode]);

  // Save view mode to cookies when it changes
  const handleViewModeChange = (newViewMode) => {
    const userId = getUserId();
    if (userId) {
      const currentViewModes = cookies.userViewMode || {};
      setCookie(
        "userViewMode",
        {
          ...currentViewModes,
          [userId]: newViewMode,
        },
        {
          path: "/",
          maxAge: 365 * 24 * 60 * 60, // 1 year
          sameSite: "strict",
        }
      );
    }
    setViewMode(newViewMode);
  };

  const statusCounts = statuses.map((s) => ({
    ...s,
    count: orders.filter((o) => o.stat === s.id).length,
  }));

  const filteredOrders = selectedStatus
    ? orders.filter((o) => o.stat === selectedStatus.id)
    : orders;

  const getStatus = (status) =>
    statuses.find((s) => s.id === status) || { color: "#757575" };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
    setSelectedOrder(selectedOrder === id ? null : id);
  };

  const hasSbqQte = (order) => order.sbqQte && order.sbqQte > 0;

  return (
    <Box width="320px" height="100%" display="flex" flexDirection="column">
      {/* Status Chips */}
      <Box mb={1}>
        <Grid container spacing={"8px"} style={{ gap: "8px" }}>
          {statusCounts.map(
            (status) =>
              status.count > 0 && (
                <Grid item key={status.label}>
                  <Chip
                    size="small"
                    label={`${status.label} (${status.count})`}
                    style={{
                      backgroundColor: status.color,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: "28px",
                      minWidth: "70px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }}
                    onClick={() => setSelectedStatus(status)}
                    clickable
                  />
                </Grid>
              )
          )}
        </Grid>
      </Box>

      {/* Orders Container */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        bgcolor="#fafafa"
        borderRadius="8px"
        boxShadow="0 2px 8px rgba(0,0,0,0.1)"
        overflow="hidden"
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          bgcolor="white"
          style={{ borderBottom: "1px solid #ccc", marginBottom: "8px" }}
        >
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 700, fontSize: "0.85rem" }}
            noWrap
          >
            {selectedStatus ? selectedStatus.label : "Toutes les commandes"}(
            {filteredOrders.length})
          </Typography>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              style={{ padding: 6, marginRight: 4 }}
              onClick={() =>
                handleViewModeChange(viewMode === "cards" ? "table" : "cards")
              }
            >
              {viewMode === "cards" ? (
                <ViewList style={{ fontSize: 20 }} />
              ) : (
                <ViewModule style={{ fontSize: 20 }} />
              )}
            </IconButton>
            {selectedStatus ? (
              <IconButton
                size="small"
                style={{ padding: 6 }}
                onClick={() => setSelectedStatus(null)}
              >
                <Close style={{ fontSize: 20 }} />
              </IconButton>
            ) : (
              <></>
            )}
          </Box>
        </Box>
        {/* Scrollable Content */}
        <Box flex={1} overflow="auto" minHeight={0}>
          {filteredOrders.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Aucune commande trouvée
              </Typography>
            </Box>
          ) : viewMode === "cards" ? (
            <Box p={0.8}>
              {filteredOrders.map((order, index) => {
                const isExpanded = expandedRow === order.id;
                const isSelected = selectedOrder === order.id;
                const statusColor = getStatus(order.stat).color;
                const statusLabel = getStatus(order.stat).label;
                const backgroundColor = isSelected
                  ? getSelectedBackgroundColor(statusColor)
                  : getLightBackgroundColor(statusColor);
                const hasSbq = hasSbqQte(order);

                return (
                  <Card
                    key={order.id}
                    elevation={hovered === index ? 2 : 0}
                    style={{
                      borderLeft: `4px solid ${statusColor}`,
                      marginBottom: 6,
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      backgroundColor: backgroundColor,
                    }}
                    onMouseEnter={() => setHovered(index)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => toggleRow(order.id)}
                  >
                    <CardContent style={{ padding: "8px" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* Bundle Image */}
                        <Card
                          style={{
                            width: "auto",
                            height: 50,
                            borderRadius: "6px",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            flexShrink: 0,
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={
                              order.bundle
                                ? !order.card
                                  ? param.imgBdl + order.bundle.id + ".png"
                                  : param.imgDigital +
                                    "prod_" +
                                    order.bundle.id +
                                    ".png"
                                : ""
                            }
                            style={{
                              height: "100%",
                              width: "auto",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </Card>

                        {/* Main Content */}
                        <Box flex={1} minWidth={0} marginLeft={"6px"}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Typography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                color: "#333",
                                lineHeight: 1.2,
                              }}
                              noWrap
                            >
                              {order.bundle ? order.bundle.bundle : ""}
                            </Typography>

                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              width={"40px"}
                              justifyContent={"flex-end"}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: "0.75rem",
                                  color: hasSbq ? "#d32f2f" : "#666",
                                  fontWeight: hasSbq ? 700 : 500,
                                  textAlign: "end",
                                }}
                              >
                                {hasSbq ? "+" : ""} {order.qte}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box
                              display="flex"
                              flexDirection={"column"}
                              alignItems="flex-start"
                            >
                              <Tooltip title="Heure de commande">
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontSize: "0.7rem",
                                    color: "#666",
                                  }}
                                >
                                  {order.date_order.split(" ")[1]}
                                </Typography>
                              </Tooltip>
                              {order.date_transfert ? (
                                <Tooltip title="Heure de transfert">
                                  <Typography
                                    variant="caption"
                                    style={{
                                      fontSize: "0.7rem",
                                      color: statusColor,
                                    }}
                                  >
                                    {order.date_transfert.split(" ")[1]}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                ""
                              )}

                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#666",
                                }}
                              >
                                {formatPhoneNumber(order.numero)}
                              </Typography>
                            </Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            ></Box>
                          </Box>
                        </Box>

                        {/* Expand Button */}
                        <IconButton
                          size="small"
                          style={{
                            padding: 4,
                            alignSelf: "flex-start",
                          }}
                        >
                          {isExpanded ? (
                            <ExpandLess style={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMore style={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Box>

                      {/* Expanded Details */}
                      <Collapse in={isExpanded}>
                        <Box
                          mt={1}
                          pt={1}
                          borderTop="1px solid rgba(0,0,0,0.1)"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: "4px" }}
                          >
                            <Box
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: statusColor,
                              }}
                            />
                            <Typography
                              variant="caption"
                              style={{
                                fontSize: "0.65rem",
                                color: "#666",
                              }}
                            >
                              {statusLabel}
                            </Typography>
                          </Box>

                          {hasSbq && (
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              mb={1}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#d32f2f",
                                  fontWeight: 600,
                                }}
                              >
                                Sabbaqli: {order.sbqQte}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            // Table view
            <Box overflow="auto">
              <Table style={{ minWidth: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        padding: "8px 6px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "8px 6px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      Téléphone
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "8px 6px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                      align="right"
                    >
                      Qté
                    </TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const isExpanded = expandedRow === order.id;
                    const isSelected = selectedOrder === order.id;
                    const statusColor = getStatus(order.stat).color;
                    const backgroundColor = isSelected
                      ? getSelectedBackgroundColor(statusColor)
                      : getLightBackgroundColor(statusColor);
                    const hasSbq = hasSbqQte(order);
                    console.log(order);
                    return (
                      <React.Fragment key={order.id}>
                        <TableRow
                          hover
                          style={{
                            cursor: "pointer",
                            backgroundColor: backgroundColor,
                            borderLeft: `3px solid ${statusColor}`,
                          }}
                          onClick={() => toggleRow(order.id)}
                        >
                          <TableCell
                            style={{
                              //padding: "8px 6px",
                              fontSize: "0.75rem",
                              color: "#333",
                            }}
                          >
                            <Box sx={{ display: "grid" }}>
                              <Tooltip title="Heure de commande">
                                <span>{order.date_order?.split(" ")[1]}</span>
                              </Tooltip>

                              {order.date_transfert && (
                                <Tooltip title="Heure de transfert">
                                  <span style={{ color: statusColor }}>
                                    {order.date_transfert.split(" ")[1]}
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell
                            style={{
                              padding: "8px 6px",
                              fontSize: "0.75rem",
                              color: "#333",
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <img
                                src={
                                  order.bundle
                                    ? !order.card
                                      ? param.imgBdl + order.bundle.id + ".png"
                                      : param.imgDigital +
                                        "prod_" +
                                        order.bundle.id +
                                        ".png"
                                    : ""
                                }
                                alt=""
                                height="30"
                                style={{
                                  marginRight: 8,
                                  objectFit: "contain",
                                  borderRadius: "4px",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                              <Typography
                                noWrap
                                style={{
                                  fontSize: "0.75rem",
                                }}
                              >
                                {formatPhoneNumber(order.numero)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{
                              padding: "8px 6px",
                              fontSize: "0.75rem",
                              color: hasSbq ? "#d32f2f" : "#333",
                              fontWeight: hasSbq ? 700 : 400,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              {hasSbq && (
                                <Typography
                                  style={{
                                    fontSize: 14,
                                    color: "#d32f2f",
                                  }}
                                >
                                  +
                                </Typography>
                              )}
                              {order.qte}
                            </Box>
                          </TableCell>
                          <TableCell padding="checkbox">
                            {isExpanded ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row */}
                        <TableRow
                          style={{
                            cursor: "pointer",
                            backgroundColor: backgroundColor,
                            borderLeft: `3px solid ${statusColor}`,
                            borderBottom: isExpanded
                              ? `1px solid #ccc`
                              : "none",
                          }}
                        >
                          <TableCell
                            colSpan={5}
                            style={{ padding: 0, border: "none" }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box p={1.5}>
                                <Typography
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    marginBottom: 1,
                                  }}
                                >
                                  Bundle:{" "}
                                  {order.bundle ? order.bundle.bundle : ""}
                                </Typography>

                                {hasSbq && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    mb={1}
                                  >
                                    <Typography
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#d32f2f",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Sabbaqli: {order.sbqQte}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
